import { Box, InputAdornment, InputLabel, Radio, TextField } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { ReactComponent as ArrowDown } from "assets/ArrowDown.svg";
import { ReactComponent as SearchIcon } from "assets/searchicon.svg";
import ThemeContext from "components/app/Context";
import { isFunction } from "lodash";
import * as React from "react";
import { useEffect } from "react";
import { tokens } from "styles/Tokens";
import { getPropertyName } from "utils/CommonFunctions";
import Button from "../Button";
import CheckBox from "../Checkbox";
import Typography from "../Typography";
import Validation from "../Validation";
import "./MuiSelect.scss";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    className: "custom-menus",
    sx: {
      overflow: "hidden",
      border: `1px solid ${tokens.Border.General.Tertiary}`,
      boxShadow: "0px 10px 25px rgba(100, 116, 139, 0.1), 0px 20px 40px 8px rgba(100, 116, 139, 0.3)",
      borderRadius: "8px",
      "& .MuiMenuItem-root:hover": {
        backgroundColor: tokens.Background.General.Color,
        color: tokens.Text.General.Color
      },
      "& .MuiMenuItem-root.Mui-selected": {
        backgroundColor: tokens.Background.General.Color,
        color: tokens.Text.General.Color
      },
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP
    }
  }
};

interface Props {
  options: any;
  onChange: any;
  label: string;
  initialValues?: string[];
  name?: string;
  register?: (val: any) => void;
  error?: any;
  validations?: any;
  isDisabled?: boolean;
  minWidth?: string;
  isFormInput?: boolean;
  inputValue?: string;
  handleInputChange?: (val: any) => void;
  isInputEnabled?: boolean;
  handleClose?: () => void;
  setSelectedArr?: any;
  showInncode?: boolean;
  filterBy?: string;
  selectType?: string;
  isInitialValuesRender?: boolean;
  isBgWhite?: boolean;
}

export default function MultipleSelectCheckmarks(props: Props) {
  const {
    options,
    onChange,
    label,
    register,
    name,
    error,
    validations,
    initialValues,
    isDisabled,
    minWidth,
    isFormInput,
    handleInputChange,
    inputValue,
    isInputEnabled,
    handleClose,
    setSelectedArr,
    showInncode = false,
    filterBy,
    selectType = "multi",
    isInitialValuesRender = false,
    isBgWhite = false
  } = props;
  const [selectedOption, setSelectedOption] = React.useState<string[]>(initialValues || []);
  const [open, setOpen] = React.useState<boolean>(false);
  const { isMobileScreen } = React.useContext(ThemeContext);
  const borderColor = error ? tokens.Border.Feedback.Alert : tokens.Border.General.Tertiary;
  const formWidth = isMobileScreen ? "170px" : "100px";
  const formControlStyle = {
    marginX: isFormInput ? 0 : "4px",
    minWidth: minWidth || formWidth,
    width: !isMobileScreen && isFormInput ? "250px" : "auto",
    maxWidth: isMobileScreen && isFormInput ? "100px" : "auto"
  };
  const SelectStyles = {
    height: "44px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: isFormInput || isBgWhite ? tokens.Background.General.Primary : tokens.Background.General.Tertiary,
    color: tokens.Text.General.Secondary,
    fontWeight: isFormInput || isBgWhite ? "inherit" : 700,
    fontSize: "14px",
    boxShadow: "none",
    borderRadius: "8px",
    ".MuiSelect-icon": { alignSelf: "center", top: "45%" },
    border: isFormInput || isBgWhite ? `1px solid ${borderColor}` : `2px solid ${tokens.Border.General.Tertiary}`,

    ".MuiOutlinedInput-notchedOutline": {
      border: "none"
    },
    "&:hover": {
      backgroundColor: isBgWhite
        ? tokens.Background.General.Primary
        : !isFormInput
        ? tokens.Background.General.Secondary
        : `unset`,
      border:
        isFormInput || isBgWhite
          ? `1px solid ${tokens.Border.General.Primary}`
          : `2px solid ${tokens.Border.General.Tertiary}`
    },
    "&.Mui-focused": {
      border: isFormInput || isBgWhite ? `2px solid ${tokens.Border.General.Color}` : `inherit`
    }
  };

  useEffect(() => {
    if (!isInitialValuesRender) {
      if (initialValues?.length) {
        setSelectedOption(initialValues);
      }
    }
  }, []);

  useEffect(() => {
    if (isInitialValuesRender) {
      setSelectedOption(initialValues ?? []);
    }
  }, [JSON.stringify(initialValues)]);

  useEffect(() => {
    const filtersBy = filterBy ? filterBy : "name";
    if (isInputEnabled) {
      setSelectedArr(selectedOption.map((item: any) => options.find((op: any) => op[filtersBy] === item)));
    }
    if (isFormInput) onChange(selectedOption);
  }, [selectedOption]);

  const getButtons = () => {
    return (
      <Box
        mt={2}
        display="flex"
        justifyContent={"space-evenly"}
        sx={{
          borderTop: `1px solid ${tokens.Border.General.Tertiary}`,
          padding: "16px"
        }}
      >
        <Button
          variant="secondary"
          size={isMobileScreen ? "Small" : undefined}
          label="Clear selection"
          onClick={() => {
            setSelectedOption([]);
          }}
        ></Button>
        <Button
          sx={{
            marginLeft: "8px"
          }}
          variant="primary"
          label="OK"
          onClick={() => {
            onChange(selectedOption, name);
            handleSelectClose();
          }}
        ></Button>
      </Box>
    );
  };

  const getPlaceHolder = () => {
    const placeholder = isFormInput ? (
      <Typography variant="body1" color={tokens.Text.General.Tertiary}>
        Select one
      </Typography>
    ) : (
      <Typography component="span" variant={"body2"} color={tokens.Text.General.Secondary} showBold>
        {label}
      </Typography>
    );

    return (
      <InputLabel sx={{ top: "-4px" }} shrink={false} id="label">
        {placeholder}
      </InputLabel>
    );
  };

  const getFormLabel = () => {
    return label && isFormInput ? (
      <>
        <Typography component="span" color={tokens.Text.Feedback.Alert}>
          {validations?.required ? " * " : ""}
        </Typography>{" "}
        <Typography color={tokens.Text.General.PrimaryHeadline} textAlign="left" component="span" variant="body2">
          {label}
        </Typography>
      </>
    ) : (
      <></>
    );
  };

  const getRenderValue = (selected: any) => {
    if (isFormInput && selected.length) {
      return selected.join(", ");
    } else {
      return `${label} (${selected.length})`;
    }
  };

  const stopImmediatePropagation = (e: any) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const handleSelectClose = () => {
    if (handleClose) handleClose();
    setOpen(false);
  };

  useEffect(() => {
    window.dispatchEvent(new CustomEvent("resize"));
  }, [open === true]);

  const handleChange = (value: string) => {
    if (selectType === "single") {
      setSelectedOption([value]);
      return;
    }
    if (selectedOption.length && selectedOption.includes(value)) {
      const newArray = selectedOption.filter((item) => item !== value);
      setSelectedOption(newArray);
    } else {
      setSelectedOption([...selectedOption, value]);
    }
  };

  return (
    <Box textAlign="left" className="multi-select-component">
      {getFormLabel()}
      <FormControl
        sx={{
          marginY: 1,
          ...formControlStyle,
          "& legend": {
            display: "none"
          }
        }}
      >
        {!selectedOption.length && getPlaceHolder()}
        <Select
          IconComponent={ArrowDown}
          open={open}
          disabled={isDisabled}
          sx={SelectStyles}
          id="demo-multiple-checkbox"
          multiple
          value={selectedOption}
          onClose={handleSelectClose}
          onOpen={() => {
            setOpen(true);
          }}
          renderValue={(selected) => getRenderValue(selected)}
          MenuProps={{ ...MenuProps }}
          inputProps={isFunction(register) ? { ...register(name, validations) } : {}}
        >
          <div className="custom-menus" style={{ maxHeight: "122px", overflow: "auto" }}>
            {isInputEnabled && (
              <MenuItem
                sx={{
                  padding: "0"
                }}
                divider
                value={""}
                key="inputItem"
                onClickCapture={stopImmediatePropagation}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <TextField
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="end">
                        <SearchIcon />
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    width: "100%",
                    padding: "10px",
                    ".MuiInputBase-root": {
                      border: `1px solid ${tokens.Border.General.Tertiary}`
                    },
                    ".MuiInputBase-input.MuiOutlinedInput-input": {
                      padding: "10px"
                    }
                  }}
                  value={inputValue}
                  onChange={handleInputChange}
                />
              </MenuItem>
            )}
            {options?.map((item: any, index: any) => {
              if (!item) return <></>;
              return (
                <MenuItem
                  onKeyDown={(e: any) => {
                    e.stopPropagation();
                  }}
                  onClick={() => {
                    if (filterBy) {
                      handleChange(item[filterBy]);
                    } else {
                      handleChange(item.name);
                    }
                  }}
                  sx={{ height: "37px" }}
                  key={item.id || item.innCode || index}
                  value={item.name}
                  disabled={item.isDisabled}
                >
                  {selectType === "multi" ? (
                    <CheckBox checked={selectedOption?.indexOf(filterBy ? item[filterBy] : item?.name) > -1} />
                  ) : (
                    <Radio checked={selectedOption?.indexOf(filterBy ? item[filterBy] : item?.name) > -1}></Radio>
                  )}
                  {showInncode ? (
                    <Typography sx={{ marginX: "8px" }} variant={"body2"}>
                      {getPropertyName(item.name) ? `${getPropertyName(item.name)} (${item.name})` : `(${item.name})`}
                    </Typography>
                  ) : (
                    <Typography sx={{ marginX: "8px" }} variant={"body2"}>
                      {isInputEnabled ? `${item.name} (${item.innCode})` : item.name}
                    </Typography>
                  )}
                </MenuItem>
              );
            })}
          </div>
          {!isFormInput && <div className="footer-btns">{getButtons()}</div>}
        </Select>
        {error && <Validation name={name} type={error?.type} />}
      </FormControl>
    </Box>
  );
}
